import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';

export default function Billing() {
  const { user, profile, refreshProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [error, setError] = useState('');
  const [payments, setPayments] = useState([]);
  const [statusMsg, setStatusMsg] = useState('');

  // Wait for Razorpay SDK
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const s = params.get('status');
    if (s === 'success') {
      setStatusMsg('success');
      setTimeout(() => {
        window.history.replaceState({}, '', '/billing');
        refreshProfile?.();
      }, 100);
    }
  }, [refreshProfile]);

  const fetchPayments = useCallback(async () => {
    const { data } = await supabase
      .from('payments')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(20);
    setPayments(data || []);
  }, [user.id]);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  async function startCheckout() {
    setError(''); setLoading(true);
    try {
      if (!window.Razorpay) {
        throw new Error('Payment system loading. Refresh and try again.');
      }

      // Create subscription on server
      const { data, error } = await supabase.functions.invoke('create-subscription', { body: {} });
      if (error) throw new Error(error.message || 'Subscription creation failed');
      if (data?.error) throw new Error(data.error);
      if (!data?.subscription_id || !data?.key_id) {
        throw new Error('Server did not return subscription details.');
      }

      // Open Razorpay Checkout
      const rzp = new window.Razorpay({
        key: data.key_id,
        subscription_id: data.subscription_id,
        name: 'Trading Buddha',
        description: 'XAUUSD Pro · Monthly Subscription',
        image: '',
        prefill: {
          name: data.customer_name || '',
          email: data.customer_email || ''
        },
        theme: { color: '#C9973A' },
        handler: async function(response) {
          // Payment authorized — refresh profile to pick up webhook updates
          setStatusMsg('success');
          // Give webhook a few seconds to fire
          setTimeout(() => refreshProfile?.(), 3000);
          setTimeout(() => refreshProfile?.(), 8000);
          setTimeout(() => fetchPayments(), 5000);
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
          }
        }
      });

      rzp.on('payment.failed', function(response) {
        setError('Payment failed: ' + (response.error?.description || 'Unknown error'));
        setLoading(false);
      });

      rzp.open();
      setLoading(false);

    } catch (e) {
      setError(e.message);
      setLoading(false);
    }
  }

  async function cancelSubscription() {
    if (!window.confirm("Cancel your subscription? You'll keep access until your current billing period ends, then it will stop renewing.")) {
      return;
    }
    setError(''); setCancelLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('cancel-subscription', {
        body: { at_cycle_end: true }
      });
      if (error) throw new Error(error.message || 'Cancel failed');
      if (data?.error) throw new Error(data.error);
      setStatusMsg('cancelled');
      setTimeout(() => refreshProfile?.(), 1000);
    } catch (e) {
      setError(e.message);
    }
    setCancelLoading(false);
  }

  const subStatus = profile?.subscription_status || 'inactive';
  const periodEnd = profile?.subscription_current_period_end;
  const isActive = ['active', 'authenticated'].includes(subStatus) && periodEnd && new Date(periodEnd) > new Date();
  const isHalted = subStatus === 'halted';
  const isCancelled = ['cancelled', 'completed'].includes(subStatus);
  const isPending = ['created', 'pending'].includes(subStatus);
  const isAdmin = profile?.role === 'admin';

  let badge, badgeText;
  if (isAdmin) { badge = 'gold'; badgeText = 'ADMIN — UNLIMITED'; }
  else if (isActive) { badge = 'green'; badgeText = 'ACTIVE'; }
  else if (isHalted) { badge = 'amber'; badgeText = 'PAYMENT FAILED'; }
  else if (isPending) { badge = 'amber'; badgeText = 'PAYMENT PENDING'; }
  else if (isCancelled) { badge = 'red'; badgeText = 'CANCELED'; }
  else { badge = 'grey'; badgeText = 'NO SUBSCRIPTION'; }

  const daysRemaining = periodEnd
    ? Math.max(0, Math.ceil((new Date(periodEnd) - new Date()) / 86400000))
    : 0;

  const canSubscribe = !isActive && !isAdmin;
  const canCancel = isActive && !isAdmin;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title"><em>Billing & Subscription</em></h1>
        <p className="page-subtitle">Manage your Trading Buddha subscription</p>
      </div>

      {statusMsg === 'success' && (
        <div className="success-banner">
          ✓ Payment successful! Your subscription should activate within a few seconds. If not, refresh the page.
        </div>
      )}
      {statusMsg === 'cancelled' && (
        <div className="success-banner" style={{background:'rgba(226,162,74,0.08)',borderColor:'rgba(226,162,74,0.25)',color:'var(--amber)'}}>
          ✓ Subscription cancelled. You'll keep access until {periodEnd ? new Date(periodEnd).toLocaleDateString('en-IN', {day:'numeric',month:'short',year:'numeric'}) : 'the end of your billing period'}.
        </div>
      )}
      {error && <div className="error-banner">{error}</div>}

      {/* SUBSCRIPTION STATUS CARD */}
      <div className="card mb-24">
        <div className="flex-between" style={{flexWrap:'wrap',gap:16}}>
          <div>
            <div className="card-title" style={{marginBottom:8}}>SUBSCRIPTION STATUS</div>
            <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:8,flexWrap:'wrap'}}>
              <span className={`badge ${badge}`} style={{fontSize:12,padding:'4px 12px'}}>{badgeText}</span>
              {isActive && periodEnd && (
                <span style={{fontSize:13, color:'var(--text-3)'}}>
                  Renews <strong style={{color:'var(--gold-2)'}}>{new Date(periodEnd).toLocaleDateString('en-IN', {day:'numeric',month:'short',year:'numeric'})}</strong>
                  {' '}· {daysRemaining} days remaining
                </span>
              )}
            </div>
            {isAdmin && (
              <p style={{fontSize:13, color:'var(--text-3)'}}>You have admin access — no subscription required.</p>
            )}
            {!isAdmin && isActive && (
              <p style={{fontSize:13, color:'var(--text-3)'}}>You have full access to all platform features.</p>
            )}
            {!isAdmin && isHalted && (
              <p style={{fontSize:13, color:'var(--amber)'}}>Your last payment failed. Razorpay will retry automatically. To resolve faster, subscribe again.</p>
            )}
            {!isAdmin && isPending && (
              <p style={{fontSize:13, color:'var(--amber)'}}>Subscription payment is pending. Complete payment to activate.</p>
            )}
            {!isAdmin && !isActive && !isHalted && !isPending && (
              <p style={{fontSize:13, color:'var(--text-3)'}}>Subscribe to unlock the full platform — AI Coach, journal, analytics, and more.</p>
            )}
          </div>
        </div>

        {!isAdmin && (
          <div style={{marginTop:20,display:'flex',gap:12,flexWrap:'wrap'}}>
            {canSubscribe && (
              <button className="btn-gold" onClick={startCheckout} disabled={loading}>
                {loading ? 'Loading...' : 'Subscribe — ₹999/month'}
              </button>
            )}
            {canCancel && (
              <button className="btn-ghost" onClick={cancelSubscription} disabled={cancelLoading}>
                {cancelLoading ? 'Cancelling...' : 'Cancel Subscription'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* PLAN BENEFITS */}
      {!isActive && !isAdmin && (
        <div className="card mb-24">
          <div className="card-title">WHAT'S INCLUDED — ₹999/MONTH</div>
          <ul style={{listStyle:'none', display:'flex', flexDirection:'column', gap:10, marginTop:12}}>
            {[
              'AI Trading Coach — setup validator, trade review, performance review',
              'Full XAUUSD strategy playbook (5-condition framework)',
              'Private trade journal with tags, filters, equity curve',
              'Monte Carlo risk simulator',
              'Deep performance analytics with checklist correlation',
              'Position size calculator + risk manager',
              'Daily bias planner with accuracy tracking',
              '22 achievement badges with streak tracking',
              'Cancel anytime — no contracts'
            ].map(f => (
              <li key={f} style={{display:'flex',gap:10,fontSize:13,color:'var(--text-2)'}}>
                <span style={{color:'var(--green)'}}>✓</span>
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <div style={{marginTop:16,padding:'12px 14px',background:'var(--bg-3)',borderRadius:'var(--radius)',fontSize:12,color:'var(--text-3)'}}>
            💳 Pay securely via UPI, credit/debit cards, or net banking. Powered by Razorpay.
          </div>
        </div>
      )}

      {/* PAYMENT HISTORY */}
      {payments.length > 0 && (
        <div className="card">
          <div className="card-title">PAYMENT HISTORY</div>
          <div className="trades-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Event</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {payments.map(p => (
                  <tr key={p.id}>
                    <td className="td-mono">{new Date(p.created_at).toLocaleDateString('en-IN', {day:'numeric',month:'short',year:'numeric'})}</td>
                    <td style={{fontSize:12, color:'var(--text-3)'}}>{p.event_type?.replace(/\./g, ' · ').replace(/_/g, ' ') || '—'}</td>
                    <td className="td-mono">{p.amount ? `₹${(p.amount/100).toFixed(2)}` : '—'}</td>
                    <td>
                      <span className={`badge ${['captured','active','authenticated','paid','completed'].includes(p.status) ? 'green' : 'grey'}`}>
                        {p.status || '—'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
